// TradrQuant Quant Lab — original research & methodology whitepapers.
// Educational/methodology content explaining how the TradrQuant engine works.
// Any figures are conceptual and clearly labelled illustrative. Not financial advice.

export const TRACK_LABELS = {
  methodology: "Methodology",
  patterns: "Pattern Studies",
  tech: "Engineering & Tech",
  studies: "Market Studies",
};

export const PAPERS = [
  // ── METHODOLOGY ─────────────────────────────────────────────
  {
    slug: "confluence-terminal-dual-gate",
    track: "methodology",
    title: "The Confluence Terminal: a dual-gate approach to pattern qualification",
    subtitle:
      "Why we separate direction from quality — and how a two-independent-gate design filters weak setups before they ever reach the trader.",
    abstract:
      "Most signal tools compress everything into a single blended score, hiding the trade-off between where a move is going and how clean it is. TradrQuant instead runs two fully independent gates — a 0–6 Confluence score for structural alignment and a 0–100 Quality score for the character of the move. A setup must clear both before it prints. This paper explains the design rationale, each gate's inputs, and why decoupling direction from quality produces a more honest, tunable qualification pipeline.",
    readMins: 9,
    updated: "August 2026",
    sections: [
      {
        h: "The problem with a single score",
        p: [
          "A blended \"AI score\" answers a question no trader actually asked: it fuses direction, momentum, volume and cleanliness into one number, then asks you to trust the weighting. When that number is 78, you cannot tell whether the setup is strongly directional but choppy, or clean but barely trending. The weighting is hidden, fixed, and unfalsifiable.",
          "Our starting premise is that direction and quality are different axes and should be measured — and shown — separately.",
        ],
      },
      {
        h: "Gate one — Confluence (0–6)",
        p: ["Six independent structural checks, each contributing at most one point:"],
        list: [
          "EMA trend direction (fast/slow ribbon alignment).",
          "Major-trend alignment against the 200-EMA bias filter.",
          "Quant Force baseline direction (see the Quant Force paper).",
          "RSI momentum side (with, not against, the setup).",
          "Volume expansion versus recent average.",
          "Non-overextension against the reversal bands (not already stretched).",
        ],
      },
      {
        h: "Gate two — Quality (0–100)",
        p: [
          "The Quality gate ignores direction entirely and measures how clean the move is: momentum strength, current volume ratio versus its own average, candle body-to-range ratio (conviction versus indecision), and baseline efficiency. A strong-but-messy move and a clean-but-weak move can share the same Confluence score yet score very differently on Quality — which is exactly the information a single number destroys.",
        ],
        figure: { kind: "funnel", caption: "Dual-gate funnel: a candidate must clear both Confluence and Quality thresholds (and, if enabled, MTF) to print." },
      },
      {
        h: "Why two gates beat one",
        p: [
          "Independent gates are debuggable. If your results skew toward false starts, you raise the Quality floor; if you are missing clean continuations, you relax Confluence. With a single blended score you can only turn one dial and hope. Two gates also make the tool honest: every printed setup can state exactly which checks it passed, so the trader grades it rather than obeys it.",
        ],
      },
      {
        h: "Tuning the gates",
        p: [
          "The thresholds are exposed on purpose. Higher-volatility instruments (crypto alts) generally warrant a higher Quality floor to filter noisy false starts; smoother instruments (indices intraday) can run a lower Confluence minimum. The engine ships with sensible per-asset starting points, but they are a starting line, not an optimisation.",
        ],
      },
    ],
    keyFindings: [
      "Direction and quality are distinct axes; blending them into one score discards actionable information.",
      "Confluence (0–6) measures structural alignment; Quality (0–100) measures the cleanliness of the move.",
      "Independent gates are individually tunable and individually explainable.",
      "A setup prints only when both gates clear — and, optionally, multi-timeframe agreement.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns / thepatternsite.com (pattern reliability statistics)." },
      { text: "Wilder, J. W. — New Concepts in Technical Trading Systems (RSI, ATR)." },
      { text: "Kaufman, P. — Trading Systems and Methods (efficiency & adaptive baselines)." },
    ],
    faqs: [
      { q: "Is the Confluence score an AI model?", a: "No. It is six deterministic, rule-based structural checks that each contribute one point. There is no opaque model weighting — you can see exactly which checks passed." },
      { q: "Why 0–6 and 0–100 instead of one number?", a: "Because direction and quality are different questions. Keeping them separate lets you see a clean-but-weak setup differently from a strong-but-choppy one, and lets you tune each gate independently." },
      { q: "Can I change the thresholds?", a: "Yes. Both the minimum Confluence and minimum Quality are exposed inputs, with per-asset-class starting points documented in the indicator." },
    ],
  },
  {
    slug: "break-even-rate-vs-win-rate-expectancy",
    track: "methodology",
    title: 'Break-even rate beats "win rate": expectancy in pattern trading',
    subtitle:
      "The marketing number versus the honest one — and how to turn a pattern's real completion rate into position size and edge.",
    abstract:
      "Signal vendors advertise glossy \"win rates\" that rarely define what a win is. The academically grounded metric is the break-even failure rate — the share of pattern occurrences that fail to move even a token amount before reversing. This paper defines the metric, connects it to expectancy, and argues that surfacing a pattern's measured completion rate on every detection is a prerequisite for disciplined sizing.",
    readMins: 8,
    updated: "August 2026",
    sections: [
      {
        h: "What a win rate hides",
        p: [
          "A \"92% win rate\" is meaningless without a definition of win, a sample size, and an accounting of survivorship. Was a 0.2% move a win? Were losing occurrences excluded? Marketing numbers optimise for the screenshot, not for the trader's expectancy.",
        ],
      },
      {
        h: "The break-even failure rate",
        p: [
          "Thomas Bulkowski's widely cited work measures a break-even failure rate: the percentage of pattern occurrences that fail to move even ~5% in the expected direction before reversing. It is deliberately unglamorous, which is why it is useful. A pattern with a 15% break-even failure rate is genuinely dependable; one at 45% needs confirmation and smaller size.",
        ],
      },
      {
        h: "From rate to expectancy",
        p: [
          "Edge is not win rate alone. Expectancy = (win% × average win) − (loss% × average loss). A 40%-completion pattern with a 3R average winner is strongly profitable; a 70%-completion pattern with a 1:3 reward loses money. The completion rate is only one input into the equation that actually matters.",
        ],
        figure: { kind: "expectancy", caption: "Expectancy surface: profitable regions exist at low completion rates when average reward is high (illustrative)." },
      },
      {
        h: "How TradrQuant applies it",
        p: [
          "Where a pattern references published performance literature, we surface that measured completion rate alongside the detection rather than a marketing win rate, so you can size smaller on marginal setups and lean into high-rate ones. We do not present simulated outcomes as a verified track record.",
        ],
      },
    ],
    keyFindings: [
      "\"Win rate\" without a defined win, sample and accounting is a marketing artefact.",
      "Break-even failure rate is the honest, literature-grounded reliability metric.",
      "Expectancy — not completion rate alone — determines profitability.",
      "Showing the real rate on each detection enables odds-based position sizing.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (break-even failure rates, 38,500+ trades)." },
      { text: "Thorp, E. — The Kelly Criterion in blackjack, sports betting and the stock market (sizing to edge)." },
      { text: "Tharp, V. — Definitive Guide to Position Sizing (expectancy & R-multiples)." },
    ],
    faqs: [
      { q: "What is a good break-even rate?", a: "The strongest classical patterns show break-even failure rates around 13–20% in Bulkowski's data. But a lower-rate pattern can still be profitable if its average winner is large — expectancy decides." },
      { q: "Does TradrQuant publish its own win rate?", a: "We surface literature-grounded completion rates on detections and keep any track record clearly labelled. We do not present simulated results as verified performance." },
      { q: "How does the rate affect my sizing?", a: "Size to the odds: smaller on marginal, lower-rate setups; larger on high-rate, high-quality ones. The rate is an input to expectancy, which drives size." },
    ],
  },
  {
    slug: "multi-timeframe-confluence",
    track: "methodology",
    title: "Multi-timeframe confluence: quantifying higher-timeframe alignment",
    subtitle:
      "Turning the \"trade with the higher timeframe\" cliché into a measurable, optional gate across three configurable horizons.",
    abstract:
      "Discretionary traders know to respect the higher timeframe, but rarely quantify it. TradrQuant cross-checks trend alignment across three configurable higher timeframes using the same EMA logic as the primary chart, and exposes the result letter-by-letter so it can either be required for a setup to fire or displayed as read-only context. This paper formalises the alignment measure and its trade-offs.",
    readMins: 7,
    updated: "August 2026",
    sections: [
      {
        h: "Why higher timeframes dominate",
        p: [
          "A higher timeframe represents more capital and more participants; its trend exerts more force than a lower one. A bullish setup inside a daily downtrend is, by construction, a counter-trend trade — tradeable, but lower-conviction. Quantifying that context turns an intuition into a filter.",
        ],
      },
      {
        h: "The alignment measure",
        p: [
          "For each of three configurable higher timeframes (default 1H / 4H / Daily), the engine evaluates the same fast/slow EMA relationship used on the primary chart and records a directional letter. Full agreement — all three matching the setup direction — is the strongest context the engine can express; partial agreement is graded accordingly.",
        ],
        figure: { kind: "mtf", caption: "MTF panel: three higher-timeframe reads resolved into a letter-by-letter alignment string (illustrative)." },
      },
      {
        h: "Required versus displayed",
        p: [
          "Alignment can be a hard gate (a setup only prints on full or partial agreement) or a soft context read shown on the dashboard. Intraday index traders often prefer a shorter stack (15m/1H/4H); swing crypto traders lean on 1H/4H/D. The horizons are configurable precisely because the right stack depends on how you trade.",
        ],
      },
      {
        h: "The cost of confluence",
        p: [
          "Requiring alignment reduces frequency and can arrive slightly later to a move — the classic sensitivity/specificity trade-off. Displaying rather than requiring it keeps frequency high while still grading each setup. Neither is universally correct; the engine exposes both so the trader chooses.",
        ],
      },
    ],
    keyFindings: [
      "Higher timeframes carry more weight; alignment is worth measuring, not just intuiting.",
      "Three configurable horizons are resolved with the same EMA logic as the entry chart.",
      "Alignment can be a hard gate or a soft, displayed context read.",
      "Requiring alignment trades frequency and timing for higher conviction.",
    ],
    references: [
      { text: "Elder, A. — Trading for a Living (triple-screen / multi-timeframe framework)." },
      { text: "Murphy, J. — Technical Analysis of the Financial Markets (trend hierarchy)." },
      { text: "Kaufman, P. — Trading Systems and Methods (timeframe interaction)." },
    ],
    faqs: [
      { q: "Which timeframes should I use?", a: "Swing crypto traders commonly use 1H/4H/D; intraday index traders often use 15m/1H/4H. The three horizons are configurable to match how you actually trade." },
      { q: "Does requiring MTF alignment reduce signals?", a: "Yes — it lowers frequency and can arrive slightly later, in exchange for higher conviction. You can instead display alignment as context without making it mandatory." },
      { q: "Is the MTF logic the same as the main chart?", a: "Yes. It applies the same fast/slow EMA relationship to each higher timeframe for consistency." },
    ],
  },

  // ── ENGINEERING & TECH ──────────────────────────────────────
  {
    slug: "quant-force-efficiency-ratio-baseline",
    track: "tech",
    title: "Quant Force: an adaptive baseline built on Kaufman's Efficiency Ratio",
    subtitle:
      "How a baseline that reacts to the character of a move — not a fixed lookback — filters chop before it reaches the confluence layer.",
    abstract:
      "Fixed-lookback moving averages face an unavoidable dilemma: fast enough to catch moves means whippy in chop; smooth enough to ignore chop means late to moves. Quant Force sidesteps this by adapting its responsiveness to Kaufman's Efficiency Ratio, then wrapping the baseline in an ATR envelope and confirming direction only after a configurable run of closes beyond the band. This paper describes the construction and the noise-rejection logic.",
    readMins: 9,
    updated: "August 2026",
    sections: [
      {
        h: "The fixed-lookback dilemma",
        p: [
          "A 10-period average is responsive but noisy; a 50-period average is smooth but late. No single fixed lookback is right across regimes, because the market's character changes — trending one week, ranging the next. The baseline needs to adapt to that character rather than to the calendar.",
        ],
      },
      {
        h: "Kaufman's Efficiency Ratio",
        p: [
          "The Efficiency Ratio (ER) measures directional movement relative to total path length over a window: net change divided by the sum of absolute bar-to-bar changes. ER near 1 means a clean, directional move; ER near 0 means the market covered a lot of ground going nowhere. Quant Force uses ER to scale a smoothing constant, so the baseline accelerates and hugs price in efficient moves and flattens in chop.",
        ],
        figure: { kind: "efficiency", caption: "Efficiency Ratio → smoothing constant mapping: high ER tightens to price, low ER flattens (illustrative)." },
      },
      {
        h: "The ATR envelope and confirmation run",
        p: [
          "The adaptive baseline is wrapped in an envelope scaled by Average True Range, so the band widens in volatile conditions and tightens in calm ones. A directional flip is only confirmed after a configurable number of consecutive closes beyond the band — single-candle pokes are rejected before they ever reach the confluence layer.",
        ],
      },
      {
        h: "Where it sits in the pipeline",
        p: [
          "Quant Force is one of the six Confluence checks and also contributes to the Quality gate through its efficiency reading. It is deliberately upstream: by filtering single-candle noise at the baseline, it reduces the false starts the rest of the pipeline would otherwise have to handle.",
        ],
      },
    ],
    keyFindings: [
      "Fixed-lookback averages cannot be simultaneously fast and smooth across regimes.",
      "The Efficiency Ratio scales responsiveness to the character of the move.",
      "An ATR envelope adapts band width to volatility.",
      "Confirmation requires consecutive closes beyond the band, rejecting single-candle noise.",
    ],
    references: [
      { text: "Kaufman, P. — Trading Systems and Methods; Adaptive Moving Average (KAMA) & Efficiency Ratio." },
      { text: "Wilder, J. W. — New Concepts in Technical Trading Systems (Average True Range)." },
      { text: "Ehlers, J. — Cybernetic Analysis for Stocks and Futures (adaptive filtering)." },
    ],
    faqs: [
      { q: "Is Quant Force just a moving average?", a: "It is an adaptive baseline: its smoothing changes with Kaufman's Efficiency Ratio, and its direction only confirms after consecutive closes beyond an ATR-scaled envelope. That noise-rejection step is the key difference from a plain MA." },
      { q: "What is the Efficiency Ratio?", a: "Net directional change divided by the total path length over a window. Near 1 = clean trend; near 0 = choppy. It is used to scale how tightly the baseline tracks price." },
      { q: "Why an ATR envelope?", a: "So the confirmation band adapts to volatility — wider when the market is volatile, tighter when calm — instead of using a fixed distance." },
    ],
  },
  {
    slug: "non-repainting-confirmed-close-architecture",
    track: "tech",
    title: "Non-repainting by design: a confirmed-close signal architecture",
    subtitle:
      "Why every TradrQuant check evaluates on closed candles — and how server-side detection differs from a static script frozen on your chart.",
    abstract:
      "Repainting — signals that change or disappear after the fact — is the single most common reason traders distrust indicators. TradrQuant evaluates every check exclusively on confirmed candle closes, so a printed signal never rewrites itself. This paper explains what repainting is, the confirmed-close rule, and how running detection on our own infrastructure differs architecturally from a fixed script pasted onto a chart.",
    readMins: 8,
    updated: "August 2026",
    sections: [
      {
        h: "What repainting actually is",
        p: [
          "Repainting occurs when an indicator uses information from within an unfinished bar (or future bars, via lookahead) to draw a signal, then revises it once the bar closes. The backtest looks flawless because it was computed with information the live trader never had. It is the difference between a photograph and a memory that keeps editing itself.",
        ],
      },
      {
        h: "The confirmed-close rule",
        p: [
          "Every TradrQuant check — trend, Quant Force, RSI side, volume, non-overextension, quality — is evaluated only after a candle closes. A setup that prints on the close of a bar is fixed for that bar; it cannot be un-printed by subsequent intrabar wiggles. This costs a little immediacy in exchange for signals you can actually trust and review.",
        ],
        figure: { kind: "repaint", caption: "Intrabar candidate vs confirmed-close signal: only the closed-bar evaluation is emitted (illustrative)." },
      },
      {
        h: "Static script versus live engine",
        p: [
          "A typical DIY indicator is frozen at publication: the same fixed logic and thresholds forever, regardless of how the market changes. TradrQuant's detection runs on our own servers and data pipeline, ingesting live data continuously. The infrastructure runs whether or not a chart is open, which is what allows scanning the whole market rather than one symbol.",
        ],
      },
      {
        h: "Continuous re-validation",
        p: [
          "\"Alive\" here means the pipeline is recalibrated against real outcomes as new evidence arrives — tiers, thresholds and classifications are re-validated over time rather than backtested once and abandoned. This is an engineering posture, not a promise of returns; markets change, and a static ruleset silently decays.",
        ],
      },
    ],
    keyFindings: [
      "Repainting uses information the live trader never had; it flatters backtests and erodes trust.",
      "Every check evaluates on confirmed candle closes, so printed signals do not rewrite themselves.",
      "Server-side detection scales from one chart to the whole market and runs independently of your screen.",
      "The ruleset is periodically re-validated against outcomes rather than frozen at publication.",
    ],
    references: [
      { text: "TradingView — Pine Script documentation on repainting and lookahead behaviour." },
      { text: "Pardo, R. — The Evaluation and Optimization of Trading Strategies (out-of-sample validation)." },
      { text: "López de Prado, M. — Advances in Financial Machine Learning (backtest overfitting)." },
    ],
    faqs: [
      { q: "Does TradrQuant repaint?", a: "No. Every check evaluates only on confirmed candle closes, so a signal that has printed for a closed bar will not change or disappear afterward." },
      { q: "Is this a script I install on my chart?", a: "The free TradrQuant Pro indicator runs on TradingView, but the core scanner runs on our own servers and data pipeline — continuously, across the whole market, whether your chart is open or not." },
      { q: "What does \"continuously re-validated\" mean?", a: "The classification thresholds are checked against real outcomes over time and adjusted, rather than fixed once at publication. It is an engineering practice, not a guarantee of results." },
    ],
  },

  // ── MARKET STUDIES ──────────────────────────────────────────
  {
    slug: "market-regime-fear-greed-layer",
    track: "studies",
    title: "Market regime & the Fear/Greed layer: reading setups in context",
    subtitle:
      "Why an asset-level signal should never be evaluated in a vacuum — and how an aggregate posture gauge grades every detection.",
    abstract:
      "A textbook-perfect long in a fear-gripped, broadly bearish tape is a different trade than the same setup in a greedy, broadly bullish one. TradrQuant sits a market-wide regime layer above asset-level detection: a live Fear/Greed-style pulse reading aggregate bullish/bearish posture across the scanned universe. This paper argues for regime-aware grading and describes how the layer is used as context rather than as a signal.",
    readMins: 7,
    updated: "August 2026",
    sections: [
      {
        h: "The vacuum problem",
        p: [
          "Evaluating a single setup without market context is like reading one sentence and judging the whole book. In risk-off regimes, breakouts fail more and reversals stick; in risk-on regimes, the reverse. The same pattern carries different odds depending on the tape it prints into.",
        ],
      },
      {
        h: "A market-wide posture gauge",
        p: [
          "The regime layer aggregates directional posture across the entire scanned universe into a single Fear/Greed-style reading, alongside real-time bullish/bearish counts and active-pattern tracking. It is a breadth-style measure: not what one coin is doing, but what the market as a whole is doing right now.",
        ],
        figure: { kind: "gauge", caption: "Regime gauge over aggregate breadth: extreme-fear to extreme-greed posture across the scanned universe (illustrative)." },
      },
      {
        h: "Context, not a trigger",
        p: [
          "Critically, the regime layer grades setups — it does not generate them. A long that aligns with an improving regime is stronger than the identical long fighting a deteriorating one. Used as a trigger, a sentiment gauge is a blunt instrument; used as a grade on an already-qualified setup, it is a meaningful edge.",
        ],
      },
      {
        h: "Limitations",
        p: [
          "Sentiment and breadth gauges are lagging and can stay extreme longer than expected; extreme fear is not a timing signal on its own. The layer is one input among trend, momentum, quality and multi-timeframe context — never a standalone reason to act.",
        ],
      },
    ],
    keyFindings: [
      "The same setup carries different odds depending on the market regime it prints into.",
      "A breadth-style Fear/Greed gauge summarises aggregate posture across the scanned universe.",
      "The regime layer grades already-qualified setups; it does not generate signals.",
      "Sentiment is lagging — useful as context, not as a standalone trigger.",
    ],
    references: [
      { text: "alternative.me — Crypto Fear & Greed Index methodology." },
      { text: "Zweig, M. — Winning on Wall Street (market breadth & momentum)." },
      { text: "Lo, A. — Adaptive Markets (regime dependence of strategy performance)." },
    ],
    faqs: [
      { q: "Is the Fear/Greed layer a buy/sell signal?", a: "No. It is a context grade applied to setups the engine has already qualified — it never generates signals on its own." },
      { q: "What does the gauge measure?", a: "Aggregate bullish/bearish posture across the whole scanned universe, alongside real-time bull/bear counts and active-pattern tracking — a breadth-style read of the market's mood." },
      { q: "Can extreme fear time a bottom?", a: "Not reliably on its own — sentiment is lagging and can stay extreme. It is best used to grade setups, combined with trend, quality and timeframe context." },
    ],
  },

  // ── PATTERN STUDIES ─────────────────────────────────────────
  {
    slug: "bull-flag-pattern-study",
    track: "patterns",
    title: "The Bull Flag: anatomy, statistics and detection of a continuation pattern",
    subtitle:
      "How to read a flag as a pause inside a trend — the pole, the flag, the volume signature, and why it is one of the more dependable continuation structures.",
    abstract:
      "The bull flag is a short consolidation that slopes gently against a strong prior advance — the market catching its breath before continuing. This study breaks down its structure, summarises the reliability figures reported in the published pattern-statistics literature, and explains how TradrQuant's engine identifies a qualifying flag through trend, volume contraction and confirmed-close breakout logic.",
    readMins: 7,
    updated: "August 2026",
    sections: [
      {
        h: "Structure: pole, flag, breakout",
        p: [
          "A bull flag has three parts: a sharp, near-vertical advance (the flagpole), a tight consolidation that drifts slightly downward or sideways (the flag), and a breakout through the upper flag boundary that resumes the trend. The flag should be orderly and shallow — a deep, sloppy pullback is a different animal.",
        ],
      },
      {
        h: "The volume signature",
        p: [
          "The tell is volume: heavy on the pole, contracting through the flag, expanding again on the breakout. Falling volume during the flag signals sellers are absent rather than aggressive; the expansion on breakout confirms demand has returned. TradrQuant's volume-expansion check is one of the six confluence points precisely because this signature separates real breakouts from drift.",
        ],
        figure: { kind: "bullflag", caption: "Bull flag: steep pole, down-drifting flag on contracting volume, breakout on volume expansion (illustrative)." },
      },
      {
        h: "What the statistics say",
        p: [
          "In Thomas Bulkowski's widely cited pattern research, high-and-tight flags rank among the better-performing continuation patterns, with comparatively low break-even failure rates when the prior trend is strong and the breakout is on rising volume. As always, the completion rate is only one input to expectancy — reward relative to a sensibly placed stop matters just as much.",
        ],
      },
      {
        h: "How TradrQuant qualifies it",
        p: [
          "A flag only qualifies when the ribbon and 200-EMA confirm an established uptrend, the consolidation shows contracting range and volume, and price closes beyond the flag boundary — all evaluated on confirmed candle closes. The measured-move projection (roughly the pole height added to the breakout) informs the ATR-based TP suite, never a fixed guess.",
        ],
      },
    ],
    keyFindings: [
      "A bull flag is a shallow, orderly pause inside a strong uptrend — not a deep reversal.",
      "Volume contracts through the flag and expands on the breakout.",
      "Published statistics rank tight flags among the more dependable continuation patterns.",
      "TradrQuant requires trend, volume contraction and a confirmed-close breakout before it qualifies.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (flags, high-and-tight flags)." },
      { text: "Edwards & Magee — Technical Analysis of Stock Trends (continuation patterns)." },
      { text: "Wilder, J. W. — New Concepts in Technical Trading Systems (ATR for targets)." },
    ],
    faqs: [
      { q: "How is a bull flag different from a pennant?", a: "A flag consolidates in a small parallel channel that drifts against the trend; a pennant consolidates in a small symmetrical triangle. Both are short continuation pauses with contracting volume." },
      { q: "Where is the measured-move target?", a: "A common projection adds the flagpole height to the breakout point. TradrQuant derives its TP1/TP2/TP3 from live ATR rather than a fixed points value." },
      { q: "What invalidates the flag?", a: "A deep pullback (well past half the pole), expanding volume during the flag, or a close back below the trend structure. Deep, messy pullbacks are not flags." },
    ],
  },
  {
    slug: "cup-and-handle-pattern-study",
    track: "patterns",
    title: "The Cup & Handle: a rounded accumulation base and its breakout",
    subtitle:
      "Why the smooth U-shaped base and its short pullback handle signal orderly accumulation — and how to trade the rim breakout.",
    abstract:
      "Popularised by William O'Neil, the cup and handle is a rounded consolidation (the cup) followed by a small, downward-drifting pullback (the handle) that precedes a breakout above the rim. This study covers the ideal proportions, the accumulation psychology, the published reliability figures, and how TradrQuant detects a qualifying rim breakout.",
    readMins: 7,
    updated: "August 2026",
    sections: [
      {
        h: "Anatomy of the base",
        p: [
          "The cup is a smooth, rounded bottom — a gradual transfer from weak to strong hands — ideally U-shaped rather than a sharp V. The handle is a short, shallow drift lower near the right rim, shaking out the last impatient holders. A breakout above the rim on expanding volume completes the pattern.",
        ],
        figure: { kind: "cup", caption: "Cup & handle: rounded U-shaped base, shallow handle near the rim, breakout on volume (illustrative)." },
      },
      {
        h: "Proportions that matter",
        p: [
          "Quality bases are neither too shallow nor absurdly deep, and the handle should form in the upper half of the cup — a handle that sags too low signals underlying weakness. A rounded cup beats a jagged one because it reflects genuine, orderly accumulation rather than a violent bounce.",
        ],
      },
      {
        h: "What the statistics say",
        p: [
          "Bulkowski's published statistics report the cup with handle as a reliable bullish pattern, with a relatively low break-even failure rate in bull markets when the breakout occurs on rising volume. Reliability degrades when the handle is deep or the broader market regime is risk-off — context the engine's regime layer accounts for.",
        ],
      },
      {
        h: "How TradrQuant qualifies it",
        p: [
          "Detection favours a rounded base with an established higher-timeframe trend, a shallow handle in the upper portion, and a confirmed close above the rim with volume expansion. The regime layer grades the setup — a rim breakout in a broadly bullish tape is stronger than the same breakout fighting risk-off breadth.",
        ],
      },
    ],
    keyFindings: [
      "The cup should be a smooth, rounded U — not a sharp V.",
      "A quality handle is shallow and forms in the upper half of the cup.",
      "Published statistics rate the pattern reliable in bull markets on a volume breakout.",
      "TradrQuant confirms a rim breakout on close and grades it against market regime.",
    ],
    references: [
      { text: "O'Neil, W. — How to Make Money in Stocks (cup with handle)." },
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (cup with handle statistics)." },
      { text: "Weinstein, S. — Secrets for Profiting in Bull and Bear Markets (stage analysis / bases)." },
    ],
    faqs: [
      { q: "Does the cup need to be perfectly symmetrical?", a: "No, but a smooth, rounded U-shape signals orderly accumulation and tends to be more reliable than a sharp V-shaped recovery." },
      { q: "How deep should the handle be?", a: "Shallow and in the upper half of the cup. A handle that drifts deep into the lower half often signals weakness and lowers reliability." },
      { q: "When does the pattern fail?", a: "A failed rim breakout, a deep sagging handle, or a risk-off regime all reduce the odds. TradrQuant grades setups against market breadth for exactly this reason." },
    ],
  },
  {
    slug: "head-and-shoulders-pattern-study",
    track: "patterns",
    title: "Head & Shoulders: quantifying the classic reversal",
    subtitle:
      "Three peaks, a neckline and a volume story — how to read the market's most recognised topping pattern and its inverse.",
    abstract:
      "The head and shoulders marks a shift from uptrend to downtrend: a left shoulder, a higher head, a lower right shoulder, and a neckline break that confirms the reversal. The inverse mirrors it at bottoms. This study covers the structure, the crucial volume divergence, the published reliability figures, and how TradrQuant confirms a neckline break without repainting.",
    readMins: 7,
    updated: "August 2026",
    sections: [
      {
        h: "The three-peak structure",
        p: [
          "A classic top prints a left shoulder, a higher head, then a right shoulder that fails to exceed the head — momentum visibly fading. Connecting the two intervening lows gives the neckline. The pattern is only confirmed when price closes below that neckline; until then it is a candidate, not a signal.",
        ],
        figure: { kind: "hns", caption: "Head & shoulders top: left shoulder, higher head, lower right shoulder, neckline break (illustrative)." },
      },
      {
        h: "The volume divergence",
        p: [
          "The most reliable tops show declining volume from the left shoulder to the head to the right shoulder — buyers running out of conviction even as price makes a marginal high. Expanding volume on the neckline break adds confirmation that supply has taken control.",
        ],
      },
      {
        h: "What the statistics say",
        p: [
          "In Bulkowski's published research the head-and-shoulders top is among the more dependable reversal patterns, with a relatively low break-even failure rate once the neckline breaks. The measured move — the head-to-neckline distance projected below the break — provides a reference target that TradrQuant translates into ATR-based levels.",
        ],
      },
      {
        h: "How TradrQuant qualifies it",
        p: [
          "The engine treats the pattern as unconfirmed until a candle closes beyond the neckline, then requires momentum and quality alignment before it prints. Because every check runs on closed candles, a wick that pokes the neckline and recovers does not produce a phantom reversal signal.",
        ],
      },
    ],
    keyFindings: [
      "A right shoulder lower than the head shows fading momentum before the reversal.",
      "Declining volume across the three peaks strengthens the pattern.",
      "The signal is only valid on a confirmed neckline break — not before.",
      "TradrQuant evaluates the neckline break on closed candles, avoiding wick-driven false signals.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (head-and-shoulders tops & bottoms)." },
      { text: "Edwards & Magee — Technical Analysis of Stock Trends (reversal patterns)." },
      { text: "Murphy, J. — Technical Analysis of the Financial Markets (neckline & volume)." },
    ],
    faqs: [
      { q: "What is the inverse head and shoulders?", a: "The same structure flipped at a bottom — a trough (head) between two higher troughs (shoulders), confirmed on a close above the neckline. It signals a shift from downtrend to uptrend." },
      { q: "Do I enter before the neckline breaks?", a: "No. Until price closes beyond the neckline the pattern is unconfirmed. TradrQuant only qualifies it on a confirmed-close break." },
      { q: "How is the target measured?", a: "The head-to-neckline distance projected from the break point is the classical measured move. TradrQuant expresses targets via ATR-derived TP levels." },
    ],
  },
  {
    slug: "double-bottom-pattern-study",
    track: "patterns",
    title: "The Double Bottom: reading a two-test reversal",
    subtitle:
      "Why two failed pushes to the same low — a W-shape — often marks exhausted selling, and how the confirmation break works.",
    abstract:
      "A double bottom forms when price tests a support level twice, fails to break lower, and then rallies through the intervening peak — a W-shaped reversal signalling that sellers are exhausted. This study covers the structure, the confirmation trigger, the published reliability figures, and TradrQuant's detection approach.",
    readMins: 6,
    updated: "August 2026",
    sections: [
      {
        h: "The W-shape",
        p: [
          "Two distinct lows form at roughly the same level, separated by a moderate rally (the middle peak). The second low holding is the market saying the prior selling could not push through. The pattern confirms only when price closes above the middle peak — the confirmation line.",
        ],
        figure: { kind: "doublebottom", caption: "Double bottom: two lows at similar levels, confirmation break above the middle peak (illustrative)." },
      },
      {
        h: "Spacing and depth",
        p: [
          "The two lows should be separated enough in time to represent genuine, distinct tests rather than noise, and ideally the second low holds slightly above or at the first. A second low that undercuts deeply is closer to a failed test than a clean double bottom.",
        ],
      },
      {
        h: "What the statistics say",
        p: [
          "Bulkowski catalogues several double-bottom variants (Adam & Adam, Adam & Eve, etc.), most of which show respectable performance once the confirmation line breaks on rising volume. As with all reversals, the pattern is a candidate until that confirmation — trading the second low before the break is anticipation, not confirmation.",
        ],
      },
    ],
    keyFindings: [
      "Two failed tests of a support level signal exhausted selling.",
      "Confirmation requires a close above the middle peak, not just a second bounce.",
      "The two lows should be distinct tests at a similar level.",
      "TradrQuant treats the pattern as a candidate until the confirmation close.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (double bottoms, Adam/Eve variants)." },
      { text: "Edwards & Magee — Technical Analysis of Stock Trends (double tops & bottoms)." },
      { text: "Pring, M. — Technical Analysis Explained (reversal confirmation)." },
    ],
    faqs: [
      { q: "How close do the two lows need to be?", a: "Roughly the same level — small differences are fine. A second low that undercuts the first by a large margin is more of a failed test than a clean double bottom." },
      { q: "When is it confirmed?", a: "On a close above the middle peak (the confirmation line), ideally on expanding volume. Before that, it is only a candidate." },
      { q: "What is the double top?", a: "The mirror image at a high — two failed pushes to the same resistance, confirmed on a close below the intervening trough — signalling a bearish reversal." },
    ],
  },
  {
    slug: "ascending-triangle-pattern-study",
    track: "patterns",
    title: "The Ascending Triangle: pressure building against resistance",
    subtitle:
      "Flat resistance, rising support — how a series of higher lows into a ceiling telegraphs an eventual breakout.",
    abstract:
      "An ascending triangle forms when price makes higher lows into a flat horizontal resistance, compressing until it breaks — usually to the upside in an uptrend. This study covers the structure, the compression psychology, the published reliability figures, and how TradrQuant handles the breakout versus a false poke.",
    readMins: 6,
    updated: "August 2026",
    sections: [
      {
        h: "Flat top, rising bottom",
        p: [
          "The pattern is defined by a horizontal resistance line touched multiple times and an up-sloping support line of higher lows. Buyers are stepping in earlier each time while sellers defend a fixed ceiling — a coil that resolves when one side gives way, most often to the upside within an existing uptrend.",
        ],
        figure: { kind: "triangle", caption: "Ascending triangle: flat resistance, higher lows compressing into a breakout (illustrative)." },
      },
      {
        h: "Compression and volume",
        p: [
          "Volume typically contracts as the triangle matures and expands on the breakout. The tightening range concentrates energy; the volume expansion on the resistance break is what separates a genuine resolution from another rejection at the ceiling.",
        ],
      },
      {
        h: "What the statistics say and how we qualify it",
        p: [
          "Published statistics rank the ascending triangle a solid continuation pattern on an upside breakout, with reliability improving when the prior trend is up and volume expands on the break. TradrQuant requires a confirmed close above the horizontal resistance with volume expansion — a single wick through the ceiling that closes back inside does not qualify.",
        ],
      },
    ],
    keyFindings: [
      "Higher lows into flat resistance show buyers growing more aggressive.",
      "Range and volume contract into the apex, then expand on the breakout.",
      "Most reliable as an upside continuation within an existing uptrend.",
      "TradrQuant needs a confirmed-close resistance break with volume, not a wick.",
    ],
    references: [
      { text: "Bulkowski, T. — Encyclopedia of Chart Patterns (ascending/descending/symmetrical triangles)." },
      { text: "Edwards & Magee — Technical Analysis of Stock Trends (triangles)." },
      { text: "Murphy, J. — Technical Analysis of the Financial Markets (continuation patterns)." },
    ],
    faqs: [
      { q: "Do ascending triangles always break up?", a: "No — they are biased upward, especially in uptrends, but they can break either way. That is why confirmation on a close (not anticipation) matters." },
      { q: "How is the target estimated?", a: "The classical measured move is the triangle's height projected from the breakout. TradrQuant expresses targets via ATR-based TP levels." },
      { q: "What is a descending triangle?", a: "The bearish mirror — flat support with lower highs pressing down on it — biased toward a downside break." },
    ],
  },
];

export const FEATURED_SLUGS = [
  "confluence-terminal-dual-gate",
  "quant-force-efficiency-ratio-baseline",
  "bull-flag-pattern-study",
];

export const getPaper = (slug) => PAPERS.find((p) => p.slug === slug);
export const papersByTrack = (track) => PAPERS.filter((p) => p.track === track);
export const relatedPapers = (paper) =>
  PAPERS.filter((p) => p.track === paper.track && p.slug !== paper.slug);
export const featuredPapers = () => FEATURED_SLUGS.map(getPaper).filter(Boolean);
